import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../lib/connect/clients';
import { isPermissionDenied } from './conflictHooks';
export function EnrichmentControl({ nodeId }) {
    const queryClient = useQueryClient();
    const [rationale, setRationale] = useState('');
    const state = useQuery({
        queryKey: [
            'source-enrichment',
            nodeId
        ],
        queryFn: ()=>projectDataClient.getEnrichmentState({
                nodeId
            }),
        retry: (n, err)=>!isPermissionDenied(err) && n < 2
    });
    const refresh = async ()=>{
        await queryClient.invalidateQueries({
            queryKey: [
                'source-enrichment'
            ]
        });
        await queryClient.invalidateQueries({
            queryKey: [
                'observation-conflicts'
            ]
        });
    };
    const toggle = useMutation({
        mutationFn: (enabled)=>projectDataClient.setEnrichmentEnabled({
                enabled
            }),
        onSuccess: refresh
    });
    const reverse = useMutation({
        mutationFn: (applicationId)=>projectDataClient.reverseEnrichment({
                nodeId,
                applicationId,
                rationale
            }),
        onSuccess: async ()=>{
            setRationale('');
            await refresh();
        }
    });
    if (state.isError && isPermissionDenied(state.error)) return null;
    if (state.isPending) return /*#__PURE__*/ _jsx("output", {
        children: "Loading source enrichment…"
    });
    if (state.isError) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: "Unable to load enrichment controls."
    });
    const pending = toggle.isPending || reverse.isPending;
    const reversalError = reverse.error instanceof ConnectError && reverse.error.code === Code.FailedPrecondition && reverse.error.rawMessage.includes('Company changed after enrichment; review required') ? 'Company changed after enrichment; review required. Open the Company in Registries to review the current website before trying again.' : reverse.error?.message;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2 rounded border p-3 text-xs",
        "aria-label": "Automatic source enrichment",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Automatic enrichment is ",
                    state.data.enabled ? 'on' : 'off',
                    " for your tenant."
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Only empty Company websites from supported SAM records with a confirmed identifier and confidence of at least 95% are filled. Other observations stay available for review."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                "data-testid": Testids.sourceConflicts.enrichmentToggle,
                disabled: pending,
                onClick: ()=>toggle.mutate(!state.data.enabled),
                children: toggle.isPending ? 'Saving…' : state.data.enabled ? 'Stop automatic enrichment' : 'Enable automatic enrichment'
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Stopping takes effect when confirmed here. Observations continue to queue; enabling resumes eligible pending evidence."
            }),
            (toggle.isError || reverse.isError) && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: toggle.error?.message || reversalError
            }),
            state.data.applications.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Enrichment reversal rationale",
                        value: rationale,
                        onChange: (e)=>setRationale(e.target.value),
                        placeholder: "Why reverse this update?"
                    }),
                    state.data.applications.map((a)=>/*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.sourceConflicts.enrichmentApplication,
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        a.field,
                                        ": ",
                                        a.appliedValue,
                                        " · ",
                                        a.source,
                                        " · ",
                                        Math.round(a.confidence * 100),
                                        "% ·",
                                        ' ',
                                        a.appliedAt?.toDate().toLocaleString()
                                    ]
                                }),
                                a.reversedAt ? /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Reversed by ",
                                        a.reversedBy,
                                        " · ",
                                        a.reversalRationale,
                                        " ·",
                                        ' ',
                                        a.reversedAt.toDate().toLocaleString()
                                    ]
                                }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    "data-testid": Testids.sourceConflicts.enrichmentReverse,
                                    disabled: pending || !rationale.trim(),
                                    onClick: ()=>reverse.mutate(a.id),
                                    children: "Reverse automatic update"
                                })
                            ]
                        }, a.id))
                ]
            })
        ]
    });
}
